import { Button, useToast } from '@provenance/design';
import { DocumentStatus, type DocumentDetail } from '@provenance/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Send } from 'lucide-react';
import { ApiError, api } from '../../lib/api';

/**
 * "Send again" (T-146): the email somebody says they never got. Each offers itself only in
 * the state it belongs to, re-sends to the address on file now, and lands in the request's
 * history. None of them decides anything, so Provenir Support sees them too.
 */
export function SendAgain({ doc, stamped }: { doc: DocumentDetail; stamped: boolean }) {
  const actions: Array<{ key: string; label: string; path: string; done: string }> = [];
  if (doc.status === DocumentStatus.APPROVED && stamped) {
    actions.push({ key: 'certificate', label: 'Resend certificate email', path: 'resend-certificate', done: 'Certificate email sent' });
  }
  if (doc.status === DocumentStatus.CLARIFICATION_REQUESTED) {
    actions.push({ key: 'clarification', label: 'Resend clarification', path: 'resend-clarification', done: 'Clarification request sent' });
  }
  if (doc.status === DocumentStatus.AWAITING_ACTION && doc.awaitingConfirmation) {
    actions.push({ key: 'confirmation', label: 'Resend sender confirmation', path: 'resend-confirmation', done: 'Confirmation email sent' });
  }
  return (
    <>
      {actions.map((a) => (
        <SendButton key={a.key} documentId={doc.id} label={a.label} path={a.path} done={a.done} />
      ))}
    </>
  );
}

function SendButton({ documentId, label, path, done }: { documentId: string; label: string; path: string; done: string }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const send = useMutation({
    mutationFn: () => api.post<{ to: string }>(`/api/console/fix-it/documents/${documentId}/${path}`),
    onSuccess: (r) => {
      toast.success(done, `Sent to ${r.to}. Earlier links no longer work.`);
      void queryClient.invalidateQueries({ queryKey: ['document', documentId] });
    },
    onError: (error) => toast.error('Could not send', error instanceof ApiError ? error.message : ''),
  });
  return (
    <Button
      variant="secondary"
      leadingIcon={<Send className="size-3.5" />}
      onClick={() => send.mutate()}
      disabled={send.isPending}
    >
      {send.isPending ? 'Sending…' : label}
    </Button>
  );
}
